import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

interface ToastAction {
  label: string;
  onClick: () => void;
}
interface ToastItem {
  id: number;
  message: string;
  tone: "success" | "error" | "info";
  action?: ToastAction;
  durationMs: number;
}
interface ToastOptions {
  tone?: ToastItem["tone"];
  action?: ToastAction;
  durationMs?: number;
}
interface ToastCtx {
  push: (message: string, toneOrOptions?: ToastItem["tone"] | ToastOptions) => void;
}

const Ctx = createContext<ToastCtx>({ push: () => {} });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback<ToastCtx["push"]>((message, toneOrOptions) => {
    const id = Date.now() + Math.random();
    let tone: ToastItem["tone"] = "success";
    let action: ToastAction | undefined;
    let durationMs = 4000;
    if (typeof toneOrOptions === "string") {
      tone = toneOrOptions;
    } else if (toneOrOptions) {
      tone = toneOrOptions.tone ?? "success";
      action = toneOrOptions.action;
      durationMs = toneOrOptions.durationMs ?? (action ? 6000 : 4000);
    }
    setItems((prev) => [...prev, { id, message, tone, action, durationMs }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), durationMs);
  }, []);

  return (
    <Ctx.Provider value={{ push }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 pointer-events-none">
        {items.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto flex items-center gap-3 rounded-md border px-4 py-3 shadow-md text-sm max-w-sm ${
              t.tone === "success"
                ? "bg-accent-50 text-accent-700 border-accent-100"
                : t.tone === "error"
                ? "bg-red-50 text-red-800 border-red-200"
                : "bg-gray-900 text-white border-gray-900"
            }`}
          >
            <span className="flex-1">{t.message}</span>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action!.onClick();
                  dismiss(t.id);
                }}
                className={`shrink-0 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                  t.tone === "info"
                    ? "border-white/30 text-white hover:bg-white/10"
                    : "border-current/30 hover:bg-black/5"
                }`}
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismiss(t.id)}
              className={`shrink-0 opacity-60 hover:opacity-100 ${t.tone === "info" ? "text-white" : ""}`}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  return useContext(Ctx);
}
